import { Button, Modal, Form, Container, Row, Col } from "react-bootstrap";
import { SyntheticEvent, useEffect, useState } from "react";
import {
  NumberPlateModel,
  countries,
  numberPlateAPI,
} from "../api/numberPlateAPI";
import { Problem, ValidationProblem, formErrors } from "../api/backendApi";
import FormErrorDisplay from "../common/FormErrorDisplay";
import ErrorDisplay from "../common/ErrorDisplay";
import { TrashFill } from "react-bootstrap-icons";

interface NumberPlateModalProps {
  recId: number;
  onCancel: () => void;
  modalText: string;
  disabled: boolean;
}

export function NumberPlateModal({
  recId,
  onCancel,
  modalText,
  disabled,
}: NumberPlateModalProps) {
  const [modalTitle, setModalTitle] = useState(modalText);
  const [numberPlate, setNumberPlate] = useState<NumberPlateModel>({
    countryId: 1,
    numberPlate: "",
    trailerNumberPlate: "",
  });
  const [plates, setPlates] = useState<NumberPlateModel[]>([]);
  const [editNumberPlate, setEditNumberPlate] = useState<
    NumberPlateModel | undefined
  >();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [errors, setErrors] = useState<formErrors>({});
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    async function loadModal(recId: number) {
      loadPlates(recId);
    }
    loadModal(recId);
  }, [recId]);

  const loadPlates = async (recId: number) => {
    setLoading(true);
    const data = await numberPlateAPI.getNumberPlates(recId);

    if (data.ok) {
      setError(undefined);
      setPlates(data.value);
    } else {
      setError(data.problem);
    }

    setLoading(false);
  };

  const deletePlate = async (recId: number, plate: NumberPlateModel) => {
    setLoading(true);

    const result = await numberPlateAPI.deleteNumberPlate(recId, plate);

    if (result.ok) {
      setErrors({});
      loadPlates(recId);
    } else {
      setLoading(false);
    }
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;

    const change = { [name]: value };
    const modified: NumberPlateModel = { ...numberPlate, ...change };

    setNumberPlate(modified);
  };

  const handleSubmit = (event: SyntheticEvent) => {
    event.preventDefault();

    let call =
      isEditing && editNumberPlate
        ? numberPlateAPI.updateNumberPlate(
            recId,
            editNumberPlate.countryId,
            editNumberPlate.numberPlate ?? "",
            numberPlate,
          )
        : numberPlateAPI.createNumberPlate(recId, numberPlate);
    call.then((result) => {
      if (result.ok) {
        setErrors({});
        loadPlates(recId);
        setIsEditing(false);
        setEditNumberPlate(undefined);
        setModalTitle(modalText);
        setNumberPlate({
          countryId: 1,
          numberPlate: "",
          trailerNumberPlate: "",
        });
      } else {
        let validation = result.problem as ValidationProblem;
        if (validation.validation) {
          setErrors(validation.validation);
        } else {
          let errors: formErrors = {};
          errors[""] = [`${result.problem.title} [${result.problem.instance}]`];
          setErrors(errors);
        }
      }
    });
  };

  return (
    <>
      <Modal onHide={() => onCancel()} show={true} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <Row>
              {!disabled && (
                <Col>
                  <Form onSubmit={handleSubmit} id="numberPlateForm">
                    <FormErrorDisplay errors={errors[""]} />
                    <Form.Label htmlFor="countryId">Country</Form.Label>
                    <Form.Select
                      name="countryId"
                      id="countryId"
                      defaultValue={numberPlate.countryId}
                      onChange={handleChange}
                    >
                      {Array.from(Object.keys(countries)).map((key) => (
                        <option key={key} value={key}>
                          {countries[key as any as number]}
                        </option>
                      ))}
                    </Form.Select>
                    <FormErrorDisplay errors={errors.countryId} />

                    <Form.Label htmlFor="numberPlate">Number Plate</Form.Label>
                    <Form.Control
                      name="numberPlate"
                      type="text"
                      id="numberPlate"
                      placeholder={"Number Plate..."}
                      value={numberPlate.numberPlate}
                      onChange={handleChange}
                    />

                    <FormErrorDisplay errors={errors.numberPlate} />

                    <Form.Label htmlFor="trailerNumberPlate">
                      Trailer Number Plate
                    </Form.Label>
                    <Form.Control
                      name="trailerNumberPlate"
                      type="text"
                      id="trailerNumberPlate"
                      placeholder={"Trailer Number Plate..."}
                      value={numberPlate.trailerNumberPlate}
                      onChange={handleChange}
                    />

                    <FormErrorDisplay errors={errors.trailerNumberPlate} />
                  </Form>
                  {isEditing && (
                    <>
                      <Button
                        variant="primary"
                        className="mt-3"
                        onClick={handleSubmit}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        className="mt-3 ms-3"
                        onClick={() => {
                          setErrors({});
                          setIsEditing(false);
                          setEditNumberPlate(undefined);
                          setNumberPlate({
                            countryId: 1,
                            numberPlate: "",
                            trailerNumberPlate: "",
                          });
                          setModalTitle(modalText);
                        }}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {!isEditing && (
                    <Button
                      variant="primary"
                      form="numberPlateForm"
                      type="submit"
                      className="mt-3"
                    >
                      Add
                    </Button>
                  )}
                </Col>
              )}
              <Col>
                <div className="fixed-table-header">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Country</th>
                        <th>Number plate</th>
                        <th>Trailer number plate</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan={4}>Loading...</td>
                        </tr>
                      )}

                      {error && (
                        <tr>
                          <td colSpan={4}>
                            <ErrorDisplay error={error}></ErrorDisplay>
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        !error &&
                        (plates.length === 0 ? (
                          <tr>
                            <td colSpan={4}>No number plates...</td>
                          </tr>
                        ) : (
                          plates.map((x) => (
                            <tr
                              key={`${x.countryId}_${x.numberPlate}`}
                              onClick={() => {
                                if (!disabled) {
                                  setErrors({});
                                  setEditNumberPlate(x);
                                  setModalTitle(
                                    `Edit Number Plate ${x.numberPlate}`,
                                  );
                                  setIsEditing(true);
                                  setNumberPlate(x);
                                }
                              }}
                            >
                              <td>{countries[x.countryId]}</td>
                              <td>{x.numberPlate}</td>
                              <td>{x.trailerNumberPlate}</td>
                              <td className="text-end">
                                <TrashFill
                                  className="text-danger"
                                  height={24}
                                  width={24}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (
                                      window.confirm(
                                        "Are you sure you want to delete this number plate?",
                                      )
                                    ) {
                                      deletePlate(recId, x);
                                    }
                                  }}
                                ></TrashFill>
                              </td>
                            </tr>
                          ))
                        ))}
                    </tbody>
                  </table>
                </div>
              </Col>
            </Row>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCancel}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
